import { Component, inject, Input, OnInit } from '@angular/core';
import { EpTableComponent, EPTableConfig } from '@ep/shared';
import { BatchTableDisplay, SdkBatchTableDisplay } from '../../models/BatchTableDisplay';
import { AsyncPipe } from '@angular/common';
import { ReportsApiService } from '../../services';
import { map, Observable, of, switchMap, take } from 'rxjs';
import { Router } from '@angular/router';
import { ApplicationProfileService } from '@app/@shared/services/application-profile.service';
import { Batch, SdkBatch } from '@app/resources/services';
import { PortalTheme } from '@app/@shared/models';
import { CssValidatorService } from '@app/@shared/services/css-validator.service';
import { BreadcrumbService } from '@app/resources/services/breadcrumb.service';
import { FA_ICON } from '@shared/icons';

@Component({
  selector: 'app-existing-sdk-batch-report',
  standalone: true,
  imports: [EpTableComponent, AsyncPipe],
  template: `
    <div class="p-4">
      <app-ep-table [config]="config" [data]="batches$ | async" data-cy="batches-table" />
    </div>
  `,
  styleUrl: './existing-sdk-batch-report.component.scss',
})
export class ExistingSdkBatchReportComponent implements OnInit {
  private readonly reportService = inject(ReportsApiService);
  private readonly router = inject(Router);
  private readonly applicationProfileService = inject(ApplicationProfileService);
  private readonly cssValidatorService = inject(CssValidatorService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  protected calendarFilter: Date | undefined;

  @Input() public theme: PortalTheme | null = null;
  protected config!: EPTableConfig<SdkBatch>;

  private initializeTableConfig(): void {
    this.config = {
      header: 'Batches',
      pagination: {
        rows: 10,
        showCurrentPageReport: true,
      },
      columns: [
        {
          header: 'Batch Id',
          field: 'BatchId',
          columnType: 'string',
          defaultValue: null,
        },
        {
          header: 'Batch Date',
          field: 'BatchDate',
          columnType: 'date',
          defaultValue: null,
        },
      ],
      style: {
        'custom-header-background': this.theme?.table.header.backgroundColor || '',
        'custom-table-subheader-background': this.theme?.table.subHeaderBackgroundColor || '',
        'custom-table-subheader-text-color': this.theme?.table.subHeaderTextColor || '',
        'custom-header-text-color': this.theme?.table.header.textColor || '',
        'custom-table-body-primary-background': this.theme?.table.body.primaryBackgroundColor || '',
        'custom-table-body-secondary-background': this.theme?.table.body.secondaryBackgroundColor || '',
        'custom-table-body-text-color': this.theme?.table.body.textColor || '',
      },
      globalFilterFields: ['BatchId'],
      actions: {
        rowClick: (batch: SdkBatch) => {
          this.navigateToBatchDetail(batch.BatchId);
        },
      },
    };
  }

  protected batches$: Observable<SdkBatch[]> = of();

  protected location$: Observable<number> = this.applicationProfileService.applicationProfile$.pipe(
    switchMap((appProfile) => {
      return of(appProfile.Location.LocationId);
    })
  );

  public ngOnInit(): void {
    this.breadcrumbService.setPageBreadcrumbs([
      { label: 'Reports', icon: FA_ICON.FILE_EXPORT, command: () => this.navigateBack() },
      { label: 'Batches' },
    ]);

    this.batches$ = this.reportService.getSdkBatches().pipe(map((batches) => batches));

    this.handleSingleBatchNavigation();
    this.initializeTableConfig();
    this.setCSSCustomProperties();
  }

  private handleSingleBatchNavigation(): void {
    const queryParams = this.router.parseUrl(this.router.url).queryParams;
    if (queryParams['date']) {
      return;
    }
  }

  private navigateToBatchDetail(batchId: string): void {
    this.location$.subscribe((location) => {
      this.router.navigate([`${location}/sdk-batch-detail`, batchId]);
    });
  }

  public navigateBack(): void {
    this.location$.pipe(take(1)).subscribe((location) => {
      this.router.navigate([`${location}/reports`]);
    });
  }

  private setCSSCustomProperties(): void {
    if (this.theme) {
      // Set CSS custom properties
      document.documentElement.style.setProperty(
        '--custom-header-background',
        this.theme?.table.header.backgroundColor
      );
      document.documentElement.style.setProperty(
        '--custom-table-subheader-background',
        this.theme?.table.subHeaderBackgroundColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-subheader-text-color',
        this.theme?.table.subHeaderTextColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-header-text-color',
        this.theme?.table.header.textColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-body-primary-background',
        this.theme?.table.body.primaryBackgroundColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-body-secondary-background',
        this.theme?.table.body.secondaryBackgroundColor || ''
      );
      document.documentElement.style.setProperty(
        '--custom-table-body-text-color',
        this.theme?.table.body.textColor || ''
      );

      // Check for undefined CSS variables
      this.cssValidatorService.checkUndefinedCssVariables('ExistingBatchReportComponent');
    }
  }
}
